import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Download, Monitor, HardDrive, Cpu, Terminal, Boxes,
  ShieldCheck, ChevronDown, ChevronUp,
} from 'lucide-react';
import { Button } from './ui/button';

const BUILD_DATE = '2026-08-05';

const CORE_DOWNLOADS = [
  {
    id: 'core-windows',
    platform: 'Windows x86_64',
    icon: Monitor,
    file: 'blox-core-windows-x86_64-2026-08-05.zip',
    size: '932 KB',
    sha256: 'c01de057d7deb5a002f39c503f14a3abef2c975832fc3681679f80b9ed8d88be',
    description:
      'BLOX Core embebible para Windows. Incluye blox.h, librerías, DLL, ejemplos C y manuales.',
    profiles: ['Core', 'Core + JSON'],
  },
  {
    id: 'core-linux',
    platform: 'Linux x86_64',
    icon: HardDrive,
    file: 'blox-core-linux-x86_64-2026-08-05.zip',
    size: '591 KB',
    sha256: '0eef4da3c5330fd7a95dc4456adf39830904be201347ad8f3ecf0e5f4b4b9155',
    description:
      'BLOX Core embebible para Linux. Incluye libblox_core.so, libblox_core.a, ejemplos C y manuales.',
    profiles: ['Core', 'Core + JSON'],
  },
  {
    id: 'core-esp32',
    platform: 'ESP32-S3',
    icon: Cpu,
    file: 'blox-core-esp32-s3-2026-08-05.zip',
    size: '750 KB',
    sha256: 'b2a554d42ba1648148f1e87f776d5efe383a28e3548a3601cf21c604410ac1ea',
    description:
      'BLOX Core como componente ESP-IDF precompilado para ESP32-S3. No es firmware listo para flashear.',
    profiles: ['Core'],
  },
];

const CLI_DOWNLOADS = [
  {
    id: 'cli-windows',
    platform: 'Windows x86_64',
    icon: Monitor,
    file: 'blox-cli-windows-x86_64-2026-08-05.zip',
    size: '2.9 MB',
    sha256: '619274cf1c93f235b9a61e262b459f50d1224ffdcc0765accc7bd1fd600d86b8',
    description:
      'BLOX CLI para Windows. Incluye blox.exe, dependencias, ejemplo hello.blox y manuales.',
    profiles: ['CLI'],
  },
  {
    id: 'cli-linux',
    platform: 'Linux x86_64',
    icon: HardDrive,
    file: 'blox-cli-linux-x86_64-2026-08-05.zip',
    size: '742 KB',
    sha256: '019993d320113a8e00f75433dadd93616ea25123e2d9adf54ed2b8f99c9298a6',
    description:
      'BLOX CLI para Linux. Incluye binario blox, ejemplo hello.blox y manuales.',
    profiles: ['CLI'],
  },
];

const truncate = (s) => `${s.slice(0, 12)}…${s.slice(-6)}`;

const DownloadCard = ({ item, accent, onDownload }) => {
  const Icon = item.icon;
  const [showHash, setShowHash] = useState(false);

  const accentColors = {
    cyan: {
      border: 'border-cyan-500/30 hover:border-cyan-400',
      iconBg: 'bg-cyan-500/15 border-cyan-500/40',
      iconColor: 'text-cyan-400',
      btn: 'bg-cyan-500 hover:bg-cyan-400 text-slate-950',
    },
    emerald: {
      border: 'border-emerald-500/30 hover:border-emerald-400',
      iconBg: 'bg-emerald-500/15 border-emerald-500/40',
      iconColor: 'text-emerald-400',
      btn: 'bg-emerald-500 hover:bg-emerald-400 text-slate-950',
    },
  };
  const c = accentColors[accent];

  return (
    <div
      data-testid={`download-card-${item.id}`}
      className={`p-6 rounded-2xl bg-slate-800/40 border ${c.border} transition-all hover:-translate-y-1 flex flex-col`}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-11 h-11 rounded-lg ${c.iconBg} border flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${c.iconColor}`} />
        </div>
        <div>
          <h3 className="text-white font-semibold text-lg leading-tight">{item.platform}</h3>
          <p className="text-slate-500 text-xs font-mono">{item.size} · Build {BUILD_DATE}</p>
        </div>
      </div>

      {item.profiles && item.profiles.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`profiles-${item.id}`}>
          {item.profiles.map(p => (
            <span
              key={p}
              className={`px-2 py-0.5 rounded-full ${c.iconBg} border ${c.iconColor} text-[10px] font-mono font-semibold uppercase tracking-wider`}
            >
              {p}
            </span>
          ))}
        </div>
      )}

      <p className="text-slate-400 text-sm leading-relaxed mb-4 flex-grow">
        {item.description}
      </p>

      <button
        type="button"
        onClick={() => setShowHash(v => !v)}
        data-testid={`hash-toggle-${item.id}`}
        className="mb-4 inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors font-mono"
      >
        <ShieldCheck className="w-3 h-3" />
        SHA256 {showHash ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {showHash && (
        <code
          data-testid={`hash-value-${item.id}`}
          className="mb-4 -mt-2 block px-3 py-2 rounded-md bg-slate-950/60 border border-slate-700/50 text-[10px] text-slate-400 font-mono break-all"
        >
          {item.sha256}
        </code>
      )}
      {!showHash && (
        <code className="mb-4 -mt-2 block text-[10px] text-slate-600 font-mono">
          {truncate(item.sha256)}
        </code>
      )}

      <Button
        onClick={() => onDownload(item.file)}
        data-testid={`download-btn-${item.id}`}
        className={`w-full font-semibold ${c.btn}`}
      >
        <Download className="w-4 h-4 mr-2" />
        Descargar
      </Button>
    </div>
  );
};

const Downloads = () => {
  const [downloads, setDownloads] = useState(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${API_URL}/api/stats`);
        const data = await response.json();
        setDownloads(data.downloads);
      } catch (error) {
        // silent, no bloquea la página
      }
    };
    fetchStats();
  }, [API_URL]);

  const handleDownload = async (fileName) => {
    try {
      const response = await fetch(`${API_URL}/api/stats/download`, { method: 'POST' });
      const data = await response.json();
      if (data.downloads) setDownloads(data.downloads);
    } catch (error) {
      // silent
    }

    const link = document.createElement('a');
    link.href = `/downloads/${fileName}`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <section id="download" className="py-24 bg-slate-900 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-6xl">
        {/* HEADER */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Descargas <span className="bg-gradient-to-r from-cyan-400 to-teal-400 text-transparent bg-clip-text">BLOX ESL</span>
          </h2>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto">
            Build <span className="font-mono text-slate-200">{BUILD_DATE}</span>. Cada ZIP incluye
            <code className="font-mono text-cyan-300 mx-1">README.md</code>,
            <code className="font-mono text-cyan-300 mx-1">USER_MANUAL.md</code>,
            <code className="font-mono text-cyan-300 mx-1">USO_RAPIDO.md</code>
            y la carpeta <code className="font-mono text-cyan-300">manuals/</code>.
          </p>
          {downloads !== null && downloads > 0 && (
            <div className="mt-4 inline-flex items-center gap-2 bg-slate-800/50 border border-slate-700 rounded-full px-4 py-2">
              <Download className="w-4 h-4 text-cyan-400" />
              <span className="text-slate-300 text-sm">
                <span className="font-semibold text-white">{downloads.toLocaleString('es-PY')}</span> descargas
              </span>
            </div>
          )}
        </div>

        {/* BLOX CORE */}
        <div className="mb-20">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-medium mb-3">
                <Boxes className="w-3 h-3" />
                Producto principal
              </div>
              <h3 className="text-3xl sm:text-4xl font-bold text-white flex items-center gap-3">
                BLOX <span className="text-cyan-400">Core</span>
              </h3>
              <p className="text-slate-400 text-base mt-2 max-w-2xl">
                Biblioteca embebible con API C para integrar scripts <code className="font-mono text-cyan-300">.blox</code> dentro de hosts nativos.
              </p>
            </div>
            <Link
              to="/embedded-scripting-language"
              data-testid="downloads-learn-core"
              className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors underline underline-offset-4"
            >
              ¿Qué es BLOX Core? →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {CORE_DOWNLOADS.map(item => (
              <DownloadCard key={item.id} item={item} accent="cyan" onDownload={handleDownload} />
            ))}
          </div>
        </div>

        {/* BLOX CLI */}
        <div className="mb-16">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-medium mb-3">
                <Terminal className="w-3 h-3" />
                Herramienta derivada
              </div>
              <h3 className="text-3xl sm:text-4xl font-bold text-white">
                BLOX <span className="text-emerald-400">CLI</span>
              </h3>
              <p className="text-slate-400 text-base mt-2 max-w-2xl">
                Ejecuta programas <code className="font-mono text-emerald-300">.blox</code> desde terminal usando el mismo lenguaje y runtime del Core.
              </p>
            </div>
            <Link
              to="/cli"
              data-testid="downloads-learn-cli"
              className="text-sm text-emerald-400 hover:text-emerald-300 transition-colors underline underline-offset-4"
            >
              ¿Qué es BLOX CLI? →
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
            {CLI_DOWNLOADS.map(item => (
              <DownloadCard key={item.id} item={item} accent="emerald" onDownload={handleDownload} />
            ))}
          </div>
        </div>

        {/* INTEGRIDAD + AYUDA */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-6 rounded-2xl bg-slate-800/40 border border-slate-700/60">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck className="w-5 h-5 text-cyan-400" />
              <h4 className="text-white font-semibold text-lg">Verifica integridad</h4>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-3">
              Cada descarga muestra su hash SHA256. Puedes verificarlo tras descargar:
            </p>
            <pre className="p-3 rounded-md bg-slate-950/60 border border-slate-700/50 text-xs text-slate-300 font-mono overflow-x-auto">
              <code>{`# Windows PowerShell
Get-FileHash blox-core-windows-x86_64-2026-08-05.zip -Algorithm SHA256

# Linux / macOS
sha256sum blox-core-linux-x86_64-2026-08-05.zip`}</code>
            </pre>
          </div>

          <div className="p-6 rounded-2xl bg-slate-800/40 border border-slate-700/60">
            <div className="flex items-center gap-2 mb-3">
              <Download className="w-5 h-5 text-emerald-400" />
              <h4 className="text-white font-semibold text-lg">¿Primera vez con BLOX?</h4>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-4">
              Cada ZIP trae un archivo <code className="font-mono text-emerald-300">USO_RAPIDO.md</code> con instrucciones. Si quieres una guía general:
            </p>
            <Link to="/docs/intro/installation">
              <Button variant="outline" className="w-full border-slate-700 text-white hover:bg-slate-800">
                Ver guía de instalación
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Downloads;
